import type { Template } from "../components/TemplateCreationModal";

export type PremadeNoticeTemplate = {
  id: string;
  file: string;
  name: string;
  title: string;
  body: string;
  isUrgent?: boolean;
};

const TEMPLATE_DIR = "/assets/school/backgrounds/templates";

export const PREMADE_NOTICE_TEMPLATES: PremadeNoticeTemplate[] = [
  {
    id: "premade-aftercare",
    file: "aftercare.webp",
    name: "Aftercare",
    title: "Aftercare / Holiday Care",
    body: "Aftercare will be closed on {% date %}. Aftercare will run from {% date %} to {% date %}.",
  },
  {
    id: "premade-photos",
    file: "photos.webp",
    name: "School photographs",
    title: "Annual School Photographs",
    body: "Annual school photos {% day %}, {% date %}. Cost: R{% amount %}. Full school uniform. No tracksuits or PE attire.",
  },
  {
    id: "premade-weather",
    file: "weather.webp",
    name: "Cancelled / weather",
    title: "Extra Curricular",
    body: "Kindly note, extra-curricular activities are suspended today due to the inclement weather. Normal school hours apply.",
    isUrgent: true,
  },
  {
    id: "premade-enrollment",
    file: "enrollment.webp",
    name: "Enrollment",
    title: "Is your child returning in {% year %}?",
    body: "Please submit the return slip for your child's enrollment by {% date %}.",
  },
  {
    id: "premade-guest",
    file: "special guest.webp",
    name: "Parent talk",
    title: "{% Guest %} — parent talk",
    body: "{% Guest %} will be visiting our school to share {% topic %}. We encourage all parents to attend on {% date %}.",
  },
  {
    id: "premade-birthday",
    file: "birthday.webp",
    name: "Birthday",
    title: "Birthday",
    body: "{% Name %} is celebrating a birthday today. Please join us in wishing them a happy day.",
  },
];

export const encodePublicAssetPath = (path: string): string => {
  if (!path) return path;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return path
    .split("/")
    .map((part, index) => (index === 0 ? part : encodeURIComponent(part)))
    .join("/");
};

export const premadeNoticeTemplatePath = (file: string): string =>
  `${TEMPLATE_DIR}/${file}`;

export const premadeNoticeTemplateSrc = (file: string): string =>
  `${process.env.PUBLIC_URL || ""}${encodePublicAssetPath(
    premadeNoticeTemplatePath(file)
  )}`;

const fillSchoolName = (text: string, schoolName: string): string =>
  text
    .replaceAll("{% school %}", schoolName)
    .replaceAll("{school}", schoolName);

export const isPremadeNoticeTemplate = (
  template: Pick<Template, "id" | "isPremade" | "organizationId">
): boolean =>
  Boolean(template.isPremade) ||
  String(template.id || "").startsWith("premade-");

export const premadeTemplatesAsDashboardTemplates = (
  schoolName: string
): Template[] =>
  PREMADE_NOTICE_TEMPLATES.map((item) => ({
    id: item.id,
    templateId: item.id,
    name: item.name,
    title: fillSchoolName(item.title, schoolName),
    description: fillSchoolName(item.body, schoolName),
    background: premadeNoticeTemplatePath(item.file),
    noticeType: "image_notice",
    isUrgent: Boolean(item.isUrgent),
    createdAt: "2026-08-13T00:00:00.000Z",
    usageCount: 0,
    organizationId: "SYSTEM",
    isPremade: true,
  }));

export const isDashboardPublicAssetUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  const trimmed = url.trim();
  if (trimmed.startsWith("/assets/")) return true;
  try {
    const parsed = new URL(trimmed, window.location.origin);
    return parsed.pathname.startsWith("/assets/");
  } catch {
    return false;
  }
};

export const dashboardPublicAssetFetchPath = (url: string): string => {
  const trimmed = url.trim();
  if (trimmed.startsWith("/")) return encodePublicAssetPath(trimmed);
  try {
    return encodePublicAssetPath(new URL(trimmed).pathname);
  } catch {
    return trimmed;
  }
};

export const premadeTemplateAttachment = (
  template: Template
): {
  fileUrl: string;
  fileName: string;
  contentType: string;
  size: number;
} | null => {
  const path = template.background?.trim();
  if (!path) return null;
  const encoded = encodePublicAssetPath(path);
  return {
    fileUrl: encoded,
    fileName: decodeURIComponent(path.split("/").pop() || "cover.png"),
    contentType: "image/png",
    size: 0,
  };
};
